import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TermItem } from '../../../graphql/graphql';
import usePatient from '../../Patient/Hooks/usePatient';
import { getMomentDate } from '../../../Utils/dates.util';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export default function usePatientRegistration({
	citizenshipOptions,
	isPrivateWorkplace = false,
	defaultValues,
	getValidationScheme,
}: {
	citizenshipOptions?: TermItem[];
	isPrivateWorkplace?: boolean;
	defaultValues?: any;
	getValidationScheme: ({
		isChild,
		isBirthDateRequested,
		isGenderRequested,
	}: {
		isChild: boolean;
		isBirthDateRequested: boolean;
		isGenderRequested: boolean;
	}) => yup.ObjectSchema<any>;
}) {
	const { checkIsAdultByInsuranceNumber, checkIsAdultByBirthDate } =
		usePatient();
	const [isBirthDateRequested, setIsBirthDateRequested] = useState(false);
	const [isGenderRequested, setIsGenderRequested] = useState(false);
	const [isChild, setIsChild] = useState(false);

	const validationSchema = getValidationScheme({
		isChild,
		isBirthDateRequested,
		isGenderRequested,
	});

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const citizenshipValue = formMethods.watch('citizenship')?.value;
	const birthDateValue = formMethods.watch('birthDate');
	const insuranceNumberValue = formMethods.watch('insuranceNumber');
	const healthPlanCodeValue = formMethods.watch('healthPlanCode')?.value;

	useEffect(() => {
		if (citizenshipOptions) {
			const otherTermItem: TermItem | undefined = citizenshipOptions.find(
				item => item.value === null,
			);
			const toggleValue = otherTermItem?.id === citizenshipValue;
			setIsBirthDateRequested(toggleValue);
			setIsGenderRequested(toggleValue);
			if (toggleValue) {
				setIsChild(false);
			} else {
				if (insuranceNumberValue && insuranceNumberValue?.length >= 6) {
					setIsChild(
						!checkIsAdultByInsuranceNumber(
							insuranceNumberValue,
							isPrivateWorkplace,
						),
					);
				}
			}
		}
	}, [citizenshipValue, isPrivateWorkplace]);

	useEffect(() => {
		const birthDateInMoment = getMomentDate(birthDateValue);
		if (birthDateInMoment.isValid()) {
			setIsChild(!checkIsAdultByBirthDate(birthDateInMoment));
		} else {
			setIsChild(false);
		}
	}, [birthDateValue]);

	return {
		isBirthDateRequested,
		isGenderRequested,
		insuranceNumberValue,
		healthPlanCodeValue,
		setIsChild,
		isChild,
		formMethods,
	};
}
